import { useCallback, useEffect, useRef, useState } from "react";
import { Joyride } from "react-joyride";
import { safeStorage } from "@/lib/storage";

/**
 * First-time user tour, powered by react-joyride.
 *
 * Steps are targeted by `data-testid` attributes so the tour survives
 * component refactors. The "done" state persists in localStorage under a
 * per-tour key, so users only see each tour once (they can re-trigger via
 * the app menu — future).
 */

const STORAGE_KEY = "gcu:tour:completed";

function readCompleted() {
  return safeStorage.getJSON(STORAGE_KEY, {}) || {};
}

function writeCompleted(map) {
  safeStorage.setJSON(STORAGE_KEY, map);
}

/** Mark tour as complete without running it (e.g. user clicks Skip.) */
export function markTourComplete(tourId) {
  const map = readCompleted();
  map[tourId] = true;
  writeCompleted(map);
}

/** Clear completion so a user can replay a tour. */
export function resetTour(tourId) {
  const map = readCompleted();
  delete map[tourId];
  writeCompleted(map);
}

/** True if this tour has never run yet. */
export function shouldRunTour(tourId) {
  const map = readCompleted();
  return !map[tourId];
}

const JOY_STYLES = {
  options: {
    primaryColor: "#C85A32",
    textColor: "#1A2E24",
    backgroundColor: "#FFFFFF",
    arrowColor: "#FFFFFF",
    overlayColor: "rgba(26, 46, 36, 0.55)",
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: 12,
    padding: 20,
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
  tooltipTitle: {
    fontFamily: "'Manrope', sans-serif",
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 6,
  },
  tooltipContent: {
    fontSize: 14,
    lineHeight: 1.55,
    padding: "6px 0",
  },
  buttonNext: {
    backgroundColor: "#C85A32",
    borderRadius: 8,
    fontWeight: 600,
    padding: "8px 16px",
    fontSize: 13,
  },
  buttonBack: {
    color: "#54645B",
    marginRight: 8,
    fontSize: 13,
  },
  buttonSkip: {
    color: "#54645B",
    fontSize: 13,
  },
  buttonClose: {
    display: "none",
  },
};

const LOCALE = {
  back: "Back",
  close: "Close",
  last: "Got it",
  next: "Next",
  skip: "Skip tour",
};

export function GuidedTour({ tourId, steps, autoStart = true }) {
  const [run, setRun] = useState(false);
  const finishedRef = useRef(false);

  const start = useCallback(() => setRun(true), []);
  const stop = useCallback(() => setRun(false), []);

  useEffect(() => {
    if (!autoStart) return undefined;
    if (!shouldRunTour(tourId)) return undefined;
    // Wait for the target elements to actually mount
    const t = setTimeout(() => setRun(true), 700);
    return () => clearTimeout(t);
  }, [tourId, autoStart]);

  // Belt-and-suspenders: persist on unmount if we ever advanced past the last step.
  useEffect(() => () => {
    if (finishedRef.current) markTourComplete(tourId);
  }, [tourId]);

  const handleCallback = (data) => {
    const { status, action, type, lifecycle, index } = data;

    // Track "visited last step" so we can persist even if react-joyride emits
    // an unusual terminal signature on final click.
    if (index !== undefined && index >= steps.length - 1) {
      finishedRef.current = true;
    }

    const terminals = ["finished", "skipped", "idle", "paused", "error"];
    const isTerminal =
      terminals.includes(status) ||
      action === "close" ||
      action === "reset" ||
      action === "skip" ||
      action === "stop" ||
      type === "tour:end" ||
      (action === "next" && lifecycle === "complete" && index === steps.length - 1) ||
      (type === "step:after" && index === steps.length - 1);

    if (isTerminal) {
      markTourComplete(tourId);
      finishedRef.current = false;
      stop();
    }
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        continuous
        showSkipButton
        showProgress
        disableOverlay
        spotlightClicks
        callback={handleCallback}
        styles={JOY_STYLES}
        locale={LOCALE}
        options={{ skipBeacon: true }}
      />
      {/* Manual re-open hook (rendered but visually hidden) — future feature can wire a "Show me around" button to this. */}
      <button
        type="button"
        data-testid={`tour-start-${tourId}`}
        onClick={start}
        className="sr-only"
        aria-hidden="true"
      >
        Start tour
      </button>
    </>
  );
}

/* ---------- Steps ---------- */

// react-joyride v3: `disableBeacon: true` at step level + `skipBeacon: true`
// via <Joyride options> makes the tooltip auto-open on every step.

export const DASHBOARD_TOUR_STEPS = [
  {

    target: '[data-testid="app-logo"]',
    title: "Welcome to DS-260USA.com",
    content:
      "This is your workspace for family-based green card cases. We'll walk you through the essentials — takes 30 seconds.",
    placement: "bottom",
    disableBeacon: true,
  },
  {

    target: '[data-testid="new-case-btn"]',
    title: "Start a new case here",
    content:
      "Click this to begin an I-130 petition for a spouse, child, parent, or sibling. Every case is guided end-to-end.",
    placement: "bottom",
    disableBeacon: true,
  },
  {

    target: '[data-testid="empty-state"], [data-testid^="case-card-"]',
    title: "Your cases live here",
    content:
      "Once you create a case, you'll see it as a card with a progress bar. Click any card to pick up where you left off.",
    placement: "top",
    disableBeacon: true,
  },
  {

    target: '[data-testid="legal-disclaimer"]',
    title: "One more thing",
    content:
      "We're not a law firm — we help you prepare, but always review your forms before mailing to USCIS. That's it! Ready when you are.",
    placement: "top",
    disableBeacon: true,
  },
];

export const CASE_WORKSPACE_TOUR_STEPS = [
  {

    target: '[data-testid="roster-stage"]',
    title: "Meet your Roster Wizard",
    content:
      "Every case starts here. Upload each person's documents (petitioner, beneficiary, kids) and the platform will read them and fill your forms automatically.",
    placement: "top",
    disableBeacon: true,
  },
  {

    target: '[data-testid="smart-sort-card"]',
    title: "Or use Smart Sort",
    content:
      "In a hurry? Drop one big PDF with all your documents mixed together. Claude Sonnet 4.5 will separate them, classify each one, and file them under the right person.",
    placement: "top",
    disableBeacon: true,
  },
  {

    target: '[data-testid="roster-intro"]',
    title: "Fine-grained control",
    content:
      "Prefer to upload documents one-by-one? Expand any person's card below to upload just their documents. Either way works — mix and match.",
    placement: "top",
    disableBeacon: true,
  },
];
